import { useNavigate } from "react-router-dom";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import SearchBar from "../../components/SearchBar";
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SwitchToggle from "../../components/SwitchToggle";
import {
  useLazyGetPromotionListQuery,
  useDeletePromotionMutation,
  useUpdatePromotionMutation,
} from "../../services/promotions";
import DeleteConfirmationModal from "../../components/DeleteModal";
import Pagination from "../../components/Panigation";
import { PROMOTION_REWARD_TYPE } from "../../constants/enums";
import { isValidInput } from "../../utils/validations";
import usePermission from "../../hooks/userAccess";
import { showToast } from "../../helpers";

const ManagePromotion = () => {
  const navigate = useNavigate();

  const { isAdd, isEdit, isDelete, isView } = usePermission({
    label: "Online Brands Management",
    page_permission: "isView",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [page, setPage] = useState(1);
  const [openDelete, setOpenDelete] = useState(false);
  const [activeId, setActiveId] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(10);
  const [draggedItem, setDraggedItem] = useState<any>(null);
  const [draggedOverIndex, setDraggedOverIndex] = useState<number>(-1);
  const [isUpdatingSortOrder, setIsUpdatingSortOrder] = useState(false);

  const [triggerList, { data: listRes, isFetching }] =
    useLazyGetPromotionListQuery();
  const [deletePromotion, { isLoading: isDeleting }] =
    useDeletePromotionMutation();
  const [updatePromotion, { isLoading: isUpdating }] =
    useUpdatePromotionMutation();

  useEffect(() => {
    triggerList({
      type: 3,
      page,
      limit,
      search: debouncedSearchTerm,
      status: "default",
    });
  }, [triggerList, page, limit, debouncedSearchTerm]);

  const toggleDeleteModal = () => {
    setOpenDelete(!openDelete);
  };

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const totalItems =
    typeof (listRes as any)?.data?.count === "number"
      ? (listRes as any).data.count
      : Array.isArray((listRes as any)?.data?.RewardPromotions)
      ? (listRes as any).data.RewardPromotions.length
      : 0;
  const totalPages = Math.max(1, Math.ceil((totalItems || 0) / (limit || 10)));

  const [promotions, setPromotions] = useState<any[]>([]);

  const stripHtmlTags = (value: string | undefined | null): string => {
    if (!value) return "";
    return value.replace(/<[^>]*>/g, "").trim();
  };

  // Update promotions when data changes
  useEffect(() => {
    setTotalCount(listRes?.data?.count || 1);
    const items: any[] = Array.isArray(listRes?.data?.RewardPromotions)
      ? (listRes?.data?.RewardPromotions as any[])
      : [];
    const mappedItems = items.map((item: any, index: number) => ({
      ...item,
      id: item?._id || item?.id,
      sortOrder: item?.sortOrder ?? index + 1, // Use existing sortOrder or fallback to index
    }));

    // Sort by sortOrder to maintain consistent ordering
    mappedItems.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    setPromotions(mappedItems);
  }, [listRes]);

  const handleDelete = async () => {
    try {
      await deletePromotion({ id: activeId }).unwrap();
      toggleDeleteModal();
      triggerList({
        type: 3,
        page,
        limit,
        search: searchTerm,
        status: "default",
      });
    } catch (e) {
      console.log(e);
    }
  };

  const handleToggle = async (promo: any, nextChecked: boolean) => {
    const id = promo?._id || promo?.id;
    if (!id) return;
    const nextIsBlocked = nextChecked;
    try {
      await updatePromotion({ body: { isActive: nextIsBlocked, id } }).unwrap();
      showToast(
        `Visibility ${nextChecked ? "enabled" : "disabled"} successfully`
      );
      triggerList({
        type: 3,
        page,
        limit,
        search: searchTerm,
        status: "default",
      });
    } catch (e) {
      console.log(e);
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (e: React.DragEvent, item: any, index: number) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", "");
    setDraggedItem({ ...item, originalIndex: index });
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDraggedOverIndex(-1);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDraggedOverIndex(index);
  };

  const handleDragLeave = () => {
    setDraggedOverIndex(-1);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (!draggedItem || draggedItem.originalIndex === dropIndex) {
      setDraggedItem(null);
      setDraggedOverIndex(-1);
      return;
    }

    // Create new array with reordered items
    const reorderedPromotions = [...promotions];
    const draggedPromotion = reorderedPromotions.splice(
      draggedItem.originalIndex,
      1
    )[0];
    reorderedPromotions.splice(dropIndex, 0, draggedPromotion);

    // Update sortOrder for all items
    const updatedPromotions = reorderedPromotions.map((item, index) => ({
      ...item,
      sortOrder: index + 1,
    }));

    // Update local state immediately for better UX
    setPromotions(updatedPromotions);

    // Prepare simplified payload - only ID and target position
    const sortOrderPayload = {
      id: draggedItem.id,
      newPosition: dropIndex + 1, // 1-based position
    };

    // Log the body before sending
    console.log(
      "Sort Order Update Payload:",
      JSON.stringify(sortOrderPayload, null, 2)
    );

    try {
      setIsUpdatingSortOrder(true);
      // Call API to update sort order with simple payload
      await updatePromotion({
        body: {
          id: draggedItem.id,
          sortOrder: dropIndex + 1,
        },
      }).unwrap();

      // Refresh the list to get the latest data from server
      triggerList({
        type: 3,
        page,
        limit,
        search: debouncedSearchTerm,
        status: "default",
      });
    } catch (error) {
      console.error("Failed to update sort order:", error);
      // Revert to original state on error
      triggerList({
        type: 3,
        page,
        limit,
        search: debouncedSearchTerm,
        status: "default",
      });
    } finally {
      setIsUpdatingSortOrder(false);
    }

    setDraggedItem(null);
    setDraggedOverIndex(-1);
  };

  return (
    <>
      <div className="main_title">
        <h1>Online Brands Management</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Online Brands Management
        </p>
      </div>
      {isView && (
        <div className="cards">
          <div className="table_header">
            <div className="left_s">
              <SearchBar
                searchTerm={searchTerm}
                placeholder="Search..."
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                value={searchTerm}
                onCross={() => setSearchTerm("")}
                onChange={(val: any) => {
                  if (isValidInput(val.target.value)) {
                    setSearchTerm(val.target.value);
                  }
                }}
              />
            </div>
            <div className="right_s">
              {isAdd && (
                <Button
                  className="btn btn_primary"
                  onClick={() => navigate("/manage-promotions/add")}
                >
                  <AddIcon /> Add Online Brands
                </Button>
              )}
            </div>
          </div>
          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sort</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Visibility</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isFetching || !listRes ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <CircularProgress size={24} />
                        <span style={{ color: "#666" }}>Loading...</span>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (promotions?.length || 0) === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No data found
                    </TableCell>
                  </TableRow>
                ) : (
                  promotions?.map((promo: any, index: number) => (
                    <TableRow
                      hover
                      key={promo?.id || ""}
                      draggable={isEdit} // Only make draggable if user has edit permission
                      onDragStart={(e) => handleDragStart(e, promo, index)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      sx={{
                        cursor: isEdit ? "move" : "default",
                        opacity:
                          draggedItem && draggedItem.originalIndex === index
                            ? 0.5
                            : 1,
                        backgroundColor:
                          draggedOverIndex === index
                            ? "rgba(25, 118, 210, 0.1)"
                            : "transparent",
                        borderTop:
                          draggedOverIndex === index
                            ? "2px solid #1976d2"
                            : "none",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <TableCell>
                        <DragHandleIcon
                          className="drag_handle"
                          sx={{
                            cursor: isEdit ? "grab" : "default",
                            color: isEdit ? "inherit" : "disabled",
                            "&:active": {
                              cursor: isEdit ? "grabbing" : "default",
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box
                          component="a"
                          className="user_block click_action"
                          onClick={() =>
                            navigate(
                              `/manage-promotions/view/${promo?.id || ""}`
                            )
                          }
                        >
                          <figure className="no_radius">
                            <img
                              src={
                                promo?.image || "/static/images/placeholder.png"
                              }
                              alt={promo?.title || ""}
                            />
                          </figure>
                          <p>
                            <b>{promo?.title || ""}</b>
                            {/* <small style={{ display: 'block', fontSize: '0.75rem', color: '#666' }}>
                            Sort: {promo?.sortOrder || index + 1}
                          </small> */}
                          </p>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 250,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={stripHtmlTags(promo?.description || "")}
                      >
                        {stripHtmlTags(promo?.description || "")}
                      </TableCell>
                      <TableCell>
                        {promo?.type
                          ? promo?.type === PROMOTION_REWARD_TYPE.REWARDS
                            ? "Rewards"
                            : promo?.type === PROMOTION_REWARD_TYPE.PROMOTIONS
                            ? "Promotions"
                            : promo?.type ===
                              PROMOTION_REWARD_TYPE.ONLINE_BRANDS
                            ? "Online Brands"
                            : "In Store"
                          : ""}
                      </TableCell>
                      {/* <TableCell>
                        {promo?.startFrom || ""
                          ? new Date(
                              promo?.startFrom || ""
                            ).toLocaleDateString()
                          : "-"}{" "}
                        to{" "}
                        {promo?.expireAt || ""
                          ? new Date(promo?.expireAt || "").toLocaleDateString()
                          : "-"}
                      </TableCell> */}
                      <TableCell>
                        <SwitchToggle
                          checked={promo?.isActive}
                          onChange={(checked) => handleToggle(promo, checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="table_actions v2">
                          {/* {isView && (
                            <IconButton
                              onClick={() =>
                                navigate(
                                  `/manage-promotions/view/${promo?.id || ""}`
                                )
                              }
                            >
                              <img
                                src="/static/images/eye_icon.svg"
                                alt="View"
                              />
                            </IconButton>
                          )} */}
                          {isEdit && (
                            <IconButton
                              onClick={() =>
                                navigate(
                                  `/manage-promotions/edit/${promo?.id || ""}`
                                )
                              }
                            >
                              <img
                                src="/static/images/edit2_icon.svg"
                                alt="Edit"
                              />
                            </IconButton>
                          )}
                          {isDelete && (
                            <IconButton
                              onClick={() => {
                                setActiveId(promo?.id || "");
                                toggleDeleteModal();
                              }}
                            >
                              <img
                                src="/static/images/trash2_icon.svg"
                                alt="Delete"
                              />
                            </IconButton>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
                {isUpdatingSortOrder && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: "center", py: 2 }}>
                      <span style={{ color: "#1976d2" }}>
                        Updating sort order...
                      </span>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}
      <Pagination
        module={promotions || []}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
        onPageChange={onPageChange}
        totalPages={totalPages}
      />
      <DeleteConfirmationModal
        open={openDelete}
        onClose={toggleDeleteModal}
        handleDelete={handleDelete}
        header="Are you sure you want to delete this promotion?"
      />
    </>
  );
};

export default ManagePromotion;
